import { Testimonials } from '@/components/ui/Testimonials';
import { Link } from 'wouter';
import { Section } from '@/components/layout/Section';
import { HeroVideo } from '@/components/brand/HeroVideo';
import { Watermark } from '@/components/brand/Watermark';
import { SplitText } from '@/components/motion/SplitText';
import { Reveal } from '@/components/motion/Reveal';
import { StaggerGroup } from '@/components/motion/StaggerGroup';
import { Marquee } from '@/components/motion/Marquee';
import { Button, ArrowCircleButton } from '@/components/ui/Button';
import { BookCallButton } from '@/components/ui/BookCallButton';
import { techRowOne, techRowTwo } from '@/components/icons/tech';
import { FeaturedWork } from '@/components/ui/FeaturedWork';
import { usePageMeta } from '@/lib/usePageMeta';
import { home } from '@/content/home';
import { services } from '@/content/services';

export default function Home() {
  usePageMeta(
    'Software Development Company in Calgary',
    'Falcon Solutions is a software development company in Calgary, Alberta. Custom web and mobile app development, AI automation, QA automation and workflow automation, scoped in writing before any work starts.',
    'Home',
  );

  return (
    <>
      {/* ---------------------------------------------------------------- hero */}
      {/*
        Hero, composed around the footage rather than layered on top of it.

        The clip is a particle wave running bottom-left to top-right, leaving
        the upper-left dark and open. All type therefore sits in a single left
        column, and the decorative layers that used to fill the right side —
        the tech pattern and the watermark — are gone: with the video playing
        they were three competing textures in one frame.
      */}
      {/*
        Deliberately TALLER than the viewport (108svh), so the section below is
        just off the bottom edge and the page reads as scrollable. A hero that
        ends exactly at the fold looks like the whole page, and visitors stop.

        Height is still fixed rather than min-h: `min-h` means "at least this",
        so the content stack could grow unbounded — it previously summed to a
        constant 943px and overflowed every real screen. A set height lets the
        flex-1 column absorb the difference, and every vertical value below is
        bound to vh so the content scales rather than spilling.

        min-h-[600px] is the floor for very short windows. At 640 the CTA fell
        below the fold on a 568px-tall phone; 600 keeps it visible there while
        still giving the type room.
      */}
      <section className="relative flex min-h-[max(600px,min(850px,100svh))] flex-col overflow-hidden text-white">
        {/* Video when public/videos/hero.mp4 exists; coded mesh otherwise. */}
        <HeroVideo />

        {/*
          pt clears the 112px nav and then adds real breathing room below it —
          the headline previously started almost immediately under the bar.
          Both pads still collapse on short screens so the hero keeps fitting.
        */}
        {/*
          Wider than the .shell used by every other section: the hero is the
          one place the composition should run to the edges of a large screen,
          and at 1320px the copy stopped well short of the visual. The inner
          sections keep the standard measure, so this is the exception, not a
          change to the site's rhythm.
        */}
        <div className="relative z-10 mx-auto flex w-[min(96rem,100%-2rem)] flex-1 flex-col justify-center pt-[clamp(6.5rem,18vh,13rem)] pb-[clamp(0.5rem,1.5vh,1.5rem)] pl-[clamp(0rem,2vw,3.5rem)] md:w-[min(96rem,100%-4rem)] lg:w-[min(96rem,100%-5rem)]">
          {/*
            One column. The footage is the visual; the type sits over its dark
            left side, which is exactly the space the clip leaves open.
          */}
          <div>
            <div>
              {/* Thin rule + the company's own line, kept as the brand note. */}
              <Reveal y={12}>
                <span className="flex items-center gap-4">
                  <span className="h-px w-10 bg-accent" />
                  <span className="text-eyebrow text-white/55">{home.hero.eyebrow}</span>
                </span>
              </Reveal>

              {/*
                The size clamp is bound to BOTH axes (vw + vh) rather than width
                alone. A 1366x650 laptop is wide but short, so a vw-only rule
                keeps the type at its largest exactly where vertical space is
                scarcest — which is what used to push the buttons off the fold.
              */}
              <SplitText
                as="h1"
                text={home.hero.headline}
                /*
                  Title case, not, and lighter than the rest of the
                  site's display type: all-caps at this size reads as a poster,
                  sentence case reads as someone talking to you. h1 carries
                  font-weight 800 and font-stretch 88% globally, so both are
                  relaxed here.

                  The size is capped where the 50-character headline still sets
                  in two lines — above that cap it broke to three on a 1920
                  screen, which reads as a stack rather than a single claim.
                */
                className="mt-[clamp(1.25rem,3.6vh,3rem)] max-w-[30ch] text-[clamp(2.4rem,2.9vw+1.9vh,5rem)] leading-[1.06] font-bold tracking-[-0.024em] normal-case [font-stretch:100%]"
              />

              <Reveal delay={0.45}>
                <p className="mt-[clamp(1.1rem,3vh,2.5rem)] max-w-[56ch] text-[clamp(1.05rem,0.55vw+1vh,1.5rem)] leading-[1.6] text-white/75">
                  {home.hero.subcopy}
                </p>
              </Reveal>

              <Reveal
                delay={0.6}
                className="mt-[clamp(1.5rem,4.4vh,3.5rem)] flex flex-wrap items-center gap-4"
              >
                <BookCallButton variant="primary" size="lg">
                  {home.hero.primaryCta.label}
                </BookCallButton>
                <Button href="/portfolio" variant="ghost" size="lg" className="border border-white/35 text-white hover:bg-white/10">View our work</Button>
              </Reveal>


            </div>

            {/*
              No second visual here. The footage already fills the right side
              of the frame, and the orbit diagram sitting on top of it was two
              graphics competing in one corner. The copy takes the full width
              instead.
            */}
          </div>
        </div>

        {/*
          Baseline strip. The locations list that used to sit here is gone —
          the footer already carries both offices, and the home hero reads
          better leading with what the company does than with where it sits.
        */}
        {/* Same measure as the hero block above, so the rule lines up with it. */}
        <Reveal
          delay={0.9}
          /*
            Matches the other page heroes, which pair a pt-14 strip with the
            section's own bottom padding. Here both are vh-bound so the hero
            keeps fitting on short screens, but the proportions are the same:
            the strip rests clear of the bottom edge rather than sitting on it.
          */
          className="relative z-10 mx-auto w-[min(96rem,100%-2rem)] pt-[clamp(1.5rem,3.5vh,3.5rem)] pb-[clamp(1.5rem,4vh,4rem)] pl-[clamp(0rem,2vw,3.5rem)] md:w-[min(96rem,100%-4rem)] lg:w-[min(96rem,100%-5rem)]"
        >
          <div className="flex items-center justify-between gap-6 border-t border-white/15 pt-[clamp(0.6rem,1.4vh,1.5rem)]">
            <p className="text-xs tracking-[0.14em] text-white/55">
              {home.hero.strapline}
            </p>
            <ArrowCircleButton
              href="/contact"
              label="Contact Falcon Solutions"
              className="hidden shrink-0 sm:grid"
            />
          </div>
        </Reveal>

      </section>

      <FeaturedWork />

      {/*
        Technologies. One self-contained band rather than a <Section> plus a
        negative-margin block — that combination left a large dead gap before
        the services grid.
      */}
      <div className="border-b border-hairline bg-surface-muted py-10 md:py-12">
        <div className="shell">
          <Reveal>
            <p className="text-eyebrow text-center opacity-45">
              {home.techStrip.eyebrow}
            </p>
          </Reveal>
        </div>
        {/* space-y-10: the two rows scroll in opposite directions, so they
            need real separation or they read as one dense band. */}
        <div className="mt-7 space-y-6">
          <Marquee items={techRowOne} direction="left" speed={52} />
          <Marquee items={techRowTwo} direction="right" speed={62} />
        </div>
      </div>

      {/*
        Positioning statement.

        Previously a lone block of display type: it filled only the left of the
        section and left the right half and a band underneath empty, which read
        as a gap between the technology strip and the services grid. Now it is a
        two-column statement — the claim on the left, its substantiation on the
        right — so the section carries content across its full width.
      */}
      <Section size="sm" tone="muted">
        <div className="grid items-start gap-y-10 lg:grid-cols-12 lg:gap-x-16">
          <div className="min-w-0 lg:col-span-7">
            {/*
              Sentence case at a moderate size, not full-caps display type: in
              caps across eight lines this was a wall of shouting that was
              genuinely hard to read. Tighter leading and a slight negative
              tracking keep it feeling like a statement rather than body copy.
            */}
            <SplitText
              as="p"
              text={home.positioning.statement}
              className="font-display text-[1.75rem] leading-[1.24] font-bold tracking-[-0.015em] sm:text-[2.15rem] lg:text-[2.6rem]"
            />
          </div>

          <StaggerGroup className="min-w-0 space-y-7 lg:col-span-5 lg:pt-3" stagger={0.09}>
            {home.positioning.proof.map((point) => (
              <div key={point.title} className="border-t border-hairline pt-5">
                <h3
                  className="font-display text-base font-bold tracking-[0.02em]"
                  style={{ fontStretch: '100%' }}
                >
                  {point.title}
                </h3>
                <p className="mt-2.5 text-[0.9375rem] leading-[1.6] opacity-65">{point.body}</p>
              </div>
            ))}
          </StaggerGroup>
        </div>
      </Section>

      {/* ------------------------------------------------------------ services */}
      <Section tone="light" className="border-t border-hairline">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <p className="text-eyebrow opacity-65">What we do</p>
            <h2 className="text-display-md mt-4 max-w-[18ch]">Build, connect and improve your software</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Button href="/services" variant="secondary">
              All services
            </Button>
          </Reveal>
        </div>

        <StaggerGroup className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((service, i) => (
            <Link
              key={service.slug}
              href={`/services/${service.slug}`}
              /* The top edge picks up the brand blue on hover, so the six cards
                 have some colour of their own rather than six grey outlines. */
              className="hover-lift group flex flex-col rounded-card border border-hairline border-t-2 border-t-accent/35 bg-white p-8 shadow-card transition-colors hover:border-t-accent"
            >
              {/*
                The index leads the card on its own line. Ghosted in the corner
                it was clipped by the card edge and collided with the heading.
              */}
              <span className="mb-5 flex items-center gap-3">
                <span
                  className="font-display text-2xl leading-none font-bold text-accent-ink"
                  aria-hidden="true"
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="h-px flex-1 bg-hairline" />
              </span>
              <h3
                className="font-display text-2xl font-bold"
                style={{ fontStretch: '100%' }}
              >
                {service.name}
              </h3>
              <p className="mt-4 flex-1 text-sm leading-relaxed opacity-70">{service.summary}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-brand">
                Learn more
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4 transition-transform duration-500 ease-brand group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </span>
            </Link>
          ))}
        </StaggerGroup>
      </Section>

      {/* ------------------------------------------------------------ approach */}
      <Section tone="dark" pattern>
        <Reveal>
          <p className="text-eyebrow text-white/65">{home.approach.eyebrow}</p>
          <h2 className="text-display-md mt-4 max-w-[20ch]">{home.approach.heading}</h2>
          <p className="text-body-lg mt-6 max-w-[58ch] text-white/65">{home.approach.body}</p>
        </Reveal>

        <StaggerGroup className="mt-16 grid gap-10 sm:grid-cols-2">
          {home.approach.points.map((point, i) => (
            <div key={point.title} className="border-t border-white/15 pt-6">
              <span className="font-display text-sm text-brand-accent">
                {String(i + 1).padStart(2, '0')}
              </span>
              <h3 className="font-display mt-3 text-xl font-bold" style={{ fontStretch: '100%' }}>
                {point.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-white/60">{point.body}</p>
            </div>
          ))}
        </StaggerGroup>
      </Section>

      <Testimonials />

      {/* ----------------------------------------------------------------- cta */}
      <Section tone="gradient">
        <Watermark position="right" scale={1.5} opacity={0.05} />
        <div className="relative z-10 flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <SplitText
              as="h2"
              text={home.cta.heading}
              className="text-display-md max-w-[16ch] leading-[1.08]"
            />
            <Reveal delay={0.25}>
              <p className="text-body-lg mt-5 max-w-[48ch] text-white/70">{home.cta.body}</p>
            </Reveal>
          </div>
          <Reveal delay={0.4} className="shrink-0">
            <Button href={home.cta.button.href} variant="inverse" size="lg">
              {home.cta.button.label}
            </Button>
          </Reveal>
        </div>
      </Section>
    </>
  );
}
